import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { INCIDENTS_API } from '@/lib/incidentsApi';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RoleModEvidenceFields, evidenceContentType } from '@/pages/TriggerWorkflow';
import CatalogClarificationFields from '@/components/CatalogClarificationFields';
import { catalogApplicationPrefill, catalogClarificationView, catalogSelectionReady, catalogStepCopy, selectionApprovalVerdict, workflowInfoError } from '@/lib/menuChangeView';

function readableApiDetail(detail) {
  if (Array.isArray(detail)) {
    const text = detail.map((item) => item?.msg || item?.message || item?.detail || '').filter(Boolean).join(' ');
    return text;
  }
  const text = String(detail ?? '').trim();
  if (!text || text === '[]' || text === '{}') return '';
  return text;
}

function readFileBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || '').split(',')[1] || '');
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
import {
  ArrowLeft,
  Loader2,
  CheckCircle2,
  XCircle,
  ShieldCheck,
  ShieldAlert,
  UserPlus,
  Mail,
  Users,
  ClipboardList,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { HumanEscalationClosePanel } from '@/components/HumanEscalationClosePanel';
import WorkflowTaskGraph from '@/components/WorkflowTaskGraph';
import NupAuditPackageCard from '@/components/NupAuditPackageCard';
import {
  RoleModRoleAnalysis,
  RoleModV4Analysis,
  RoleModActionRequest,
  RoleModFailureCard,
  RoleModTransportCreated,
  RoleModDecisionHistory,
  RoleModDocuments,
} from '@/components/RoleModificationPanels';
import MenuChangeApproval from '@/components/MenuChangeApproval';
import CatalogRoleFollowUp from '@/components/CatalogRoleFollowUp';
import MenuChangeFollowUp from '@/components/MenuChangeFollowUp';
import { buildMenuFollowUpRequest, menuProgressNote, workflowProgressDetail } from '@/lib/menuChangeReview';
import { ROLE_MOD_STAGE_ACTIONS, isRoleModV4 } from '@/lib/roleModificationView';
import {
  WORKFLOW_STAGE,
  getWorkflowStageLabel,
  getWorkflowState,
  getWorkflowDisplaySteps,
  getWorkflowDisplayCurrentStep,
  getWorkflowRoles,
  getWorkflowSod,
  getApproval,
  humanApprovalRequired,
  isPendingApproval,
  needsInfo,
  getMissingFields,
  resolveWorkflowType,
  getUserConfirmation,
  isFfidAlreadyAssigned,
  getFfidHeldIds,
  getNotificationEmail,
  getNotificationEmailStatus,
  getWorkflowEscalationMessage,
  getFfidNeedsInfoBanner,
  getFfidAccessIssueBanner,
  getFfidNotifyHoldReason,
  getFfidNotificationEmailDisplay,
  isFfidHumanCloseHold,
  isRoleModHumanCloseHold,
  getRoleModEscalationBanner,
  isFfidSoftHumanHold,
  isFfidUserReportedAccessIssue,
  getRoleModFailureMessage,
  getRoleModDecisionSupport,
} from '@/lib/workflowHelpers';
import {
  WORKFLOW_TYPE_TER,
  WORKFLOW_TYPE_PWD_RESET,
  WORKFLOW_TYPE_FFID,
  WORKFLOW_TYPE_ACCESS_MOD,
  WORKFLOW_TYPE_ROLE_MOD,
  WORKFLOW_TYPE_PAMTER,
  WORKFLOW_TYPE_FIORI_CATALOG,
} from '@/lib/workflowTypes';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/contexts/PermissionsContext';
import { permissionDeniedTitle } from '@/lib/permissionUi';

const API = INCIDENTS_API;
const POLL_MS_IDLE = 4000;
const POLL_MS_ACTIVE = 1500;
const ACTIVE_STAGES = [
  WORKFLOW_STAGE.EXTRACTING,
  WORKFLOW_STAGE.CHECKING,
  WORKFLOW_STAGE.PROVISIONING,
  WORKFLOW_STAGE.TERMINATING,
  WORKFLOW_STAGE.SCHEDULED,
  WORKFLOW_STAGE.OFFBOARDING,
  WORKFLOW_STAGE.AWAITING_USER_CONFIRMATION,
];
const TERMINAL_STAGES = [
  WORKFLOW_STAGE.DONE,
  WORKFLOW_STAGE.REJECTED,
  WORKFLOW_STAGE.FAILED,
  WORKFLOW_STAGE.ESCALATED,
  WORKFLOW_STAGE.NEEDS_INFO,
  WORKFLOW_STAGE.MANUAL_REVIEW,
  WORKFLOW_STAGE.ACCESS_ISSUE_REPORTED,
  WORKFLOW_STAGE.FUT_REMEDIATION_REQUIRED,
  WORKFLOW_STAGE.CAB_REJECTED,
];

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || '').split(',')[1] || '');
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function stageBadgeClass(stage, ticket) {
  const label = String(
    (ticket && getWorkflowStageLabel(ticket)) || '',
  ).toLowerCase();
  if (label.includes('access issue') || label.includes('sap login issue')) {
    return 'bg-cp-crit text-white';
  }
  if (label === 'selection required') return 'bg-cp-indigo text-white';
  if (label === 'evidence incomplete') return 'bg-cp-mod text-white';
  if (
    label.includes('human escalation') ||
    label.includes('firefighter already assigned') ||
    label.includes('no firefighter id available') ||
    label.includes('form missing') ||
    label.includes('invalid access window')
  ) {
    return 'bg-cp-mod text-white';
  }
  switch (stage) {
    case WORKFLOW_STAGE.PENDING_APPROVAL:
      return 'bg-cp-faint text-white animate-pulse';
    case WORKFLOW_STAGE.NEEDS_INFO:
    case WORKFLOW_STAGE.ROLE_SELECTION_REQUIRED:
    case WORKFLOW_STAGE.MANUAL_REVIEW:
      return 'bg-cp-mod text-white';
    case WORKFLOW_STAGE.DONE:
      return 'bg-cp-ok text-white';
    case WORKFLOW_STAGE.SCHEDULED:
      return 'bg-cp-mod text-white';
    case WORKFLOW_STAGE.ACCESS_ISSUE_REPORTED:
      return 'bg-cp-crit text-white';
    case WORKFLOW_STAGE.ESCALATED:
      return 'bg-cp-imp text-white';
    case WORKFLOW_STAGE.REJECTED:
    case WORKFLOW_STAGE.FAILED:
    case WORKFLOW_STAGE.FUT_REMEDIATION_REQUIRED:
    case WORKFLOW_STAGE.CAB_REJECTED:
      return 'bg-cp-crit text-white';
    case WORKFLOW_STAGE.PROVISIONING:
    case WORKFLOW_STAGE.TERMINATING:
    case WORKFLOW_STAGE.OFFBOARDING:
    case WORKFLOW_STAGE.AWAITING_USER_CONFIRMATION:
      return 'bg-cp-indigo text-white';
    default:
      return 'bg-cp-indigo text-white';
  }
}

function InfoTile({ icon: Icon, label, value, missing }) {
  return (
    <div className={`rounded-lg border p-3 ${missing ? 'border-cp-imp bg-cp-inset' : ''}`}>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <div className={`mt-1 font-medium break-words ${missing ? 'text-cp-imp' : ''}`}>
        {value || (missing ? 'Missing — required' : 'Not provided')}
      </div>
    </div>
  );
}

export default function WorkflowTicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const onFfidRoute = location.pathname.startsWith('/ffid');
  // Ticket-detail NUP mutations use tickets.update (Approvals board stays on approvals.*).
  // FFID operator mutations use ffid.update via /api/ffid/* (Gateway-enforced).
  const canUpdateTicket = hasPermission('tickets', 'update');
  const canApproveMenu = hasPermission('approvals', 'update');
  const canUpdateFfid = hasPermission('ffid', 'update');
  const approver = user?.email || user?.username || 'Current User';
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [infoForm, setInfoForm] = useState({
    new_user: '',
    last_name: '',
    email: '',
    reference_user: '',
    username: '',
    termination_date: '',
    forwarding_recipient: '',
    target_role: '',
    catalog: '',
    application: '',
    source_configuration: '',
    access_strategy: '',
    copy_source: '',
    operation: '',
    note: '',
  });
  const [infoSu53File, setInfoSu53File] = useState(null);
  const [infoTraceFile, setInfoTraceFile] = useState(null);
  const [infoSubmitting, setInfoSubmitting] = useState(false);
  const [resolveComment, setResolveComment] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [manualDescription, setManualDescription] = useState('');
  const [manualEvidence, setManualEvidence] = useState(null);
  const pollRef = useRef(null);
  const [selectionApproval, setSelectionApproval] = useState(null);
  const [orgSuggestions, setOrgSuggestions] = useState(null);

  const isFfidTicket = useMemo(
    () => Boolean(ticket && resolveWorkflowType(ticket).id === WORKFLOW_TYPE_FFID),
    [ticket],
  );
  const useFfidApi = onFfidRoute || isFfidTicket;
  const canMutate = useFfidApi ? canUpdateFfid : canUpdateTicket;
  const actionBase = useFfidApi ? `${API}/ffid` : `${API}/workflow`;
  const hasCatalogRoleFollowUp = Boolean(ticket?.workflow_state?.catalog_role_follow_up);

  const fetchTicket = useCallback(async () => {
    try {
      const url = onFfidRoute ? `${API}/ffid/${id}` : `${API}/tickets/${id}`;
      const { data } = await axios.get(url);
      setTicket(data);
      return data;
    } catch (error) {
      console.error('Error fetching workflow ticket:', error);
      return null;
    } finally {
      setLoading(false);
    }
  }, [id, onFfidRoute]);

  useEffect(() => {
    fetchTicket();
  }, [fetchTicket]);

  useEffect(() => {
    if (!hasCatalogRoleFollowUp || useFfidApi) {
      setOrgSuggestions(null);
      return;
    }
    let active = true;
    axios.get(`${actionBase}/${id}/organizational-suggestions`)
      .then(({ data }) => {
        if (active) setOrgSuggestions(data);
      })
      .catch(() => {
        if (active) setOrgSuggestions({ source: 'unavailable', fields: {} });
      });
    return () => {
      active = false;
    };
  }, [actionBase, hasCatalogRoleFollowUp, id, useFfidApi]);

  // Pre-fill the missing-info form with whatever the agent already extracted.
  useEffect(() => {
    if (ticket?.workflow_stage !== WORKFLOW_STAGE.NEEDS_INFO) return;
    const extracted = getWorkflowState(ticket).extracted || {};
    const firstValue = (value) => Array.isArray(value) ? (value[0]?.value || '') : (value || '');
    setInfoForm((prev) => ({
      new_user: prev.new_user || extracted.new_user || '',
      last_name: prev.last_name || extracted.last_name || '',
      email: prev.email || extracted.email || '',
      reference_user: prev.reference_user || extracted.reference_user || '',
      username: prev.username || extracted.username || '',
      termination_date: prev.termination_date || extracted.termination_date || '',
      forwarding_recipient:
        prev.forwarding_recipient || extracted.forwarding_recipient || '',
      target_role: prev.target_role || firstValue(extracted.roles),
      catalog: prev.catalog || firstValue(extracted.catalogs),
      application: prev.application || catalogApplicationPrefill(firstValue(extracted.applications)),
      operation: prev.operation || '',
    }));
  }, [ticket]);

  // Poll while the workflow is still moving; poll faster during live step transitions.
  useEffect(() => {
    const stage = ticket?.workflow_stage;
    const isTerminal = TERMINAL_STAGES.includes(stage);
    if (isTerminal) {
      if (pollRef.current) clearInterval(pollRef.current);
      return undefined;
    }
    const intervalMs = ACTIVE_STAGES.includes(stage) ? POLL_MS_ACTIVE : POLL_MS_IDLE;
    pollRef.current = setInterval(fetchTicket, intervalMs);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [ticket?.workflow_stage, fetchTicket]);

  const handleInfoSubmit = async (options) => {
    if (!ticket || infoSubmitting || !canMutate) return;
    const approveOnSelect = options?.approve === true && canApproveMenu;
    const wfType = resolveWorkflowType(ticket);
    const isTer = wfType.id === WORKFLOW_TYPE_TER;
    const isPamter = wfType.id === WORKFLOW_TYPE_PAMTER;
    const stateNow = getWorkflowState(ticket);
    const isCatalog = stateNow.workflow_agent === WORKFLOW_TYPE_FIORI_CATALOG;
    const isRoleClarification = wfType.id === WORKFLOW_TYPE_ROLE_MOD;
    if (isCatalog) {
      const view = catalogClarificationView(stateNow.clarification || {});
      const missingCatalog = ['target_role', 'application', 'source_configuration', 'access_strategy', 'copy_source', 'catalog', 'operation']
        .filter((key) => view[key] === 'required' && !(infoForm[key] || '').trim());
      if (missingCatalog.length > 0 || !catalogSelectionReady(stateNow.clarification || {}, infoForm)) {
        toast.error('Select one of the verified choices before continuing.');
        return;
      }
    } else if (isRoleClarification) {
      const hasClarification = ['target_role', 'catalog', 'application', 'operation', 'note']
        .some((key) => (infoForm[key] || '').trim()) || Boolean(infoSu53File || infoTraceFile);
      if (!hasClarification) {
        toast.error('Add the requested information, or attach SU53 evidence or an authorization trace.');
        return;
      }
    }
    const requiredKeys = isTer
      ? ['username', 'termination_date']
      : isPamter
        ? ['username']
        : (isRoleClarification || isCatalog)
          ? []
        : ['new_user', 'last_name', 'email', 'reference_user'];
    const missing = requiredKeys.filter((key) => !(infoForm[key] || '').trim());
    if (missing.length > 0) {
      toast.error('Please fill in all required fields before submitting.');
      return;
    }
    setInfoSubmitting(true);
    try {
      const evidencePayload = {};
      if (infoSu53File) {
        evidencePayload.attachment = await readFileBase64(infoSu53File);
        evidencePayload.attachment_filename = infoSu53File.name;
        evidencePayload.attachment_content_type = evidenceContentType(infoSu53File);
      }
      if (infoTraceFile) {
        evidencePayload.trace_attachment = await readFileBase64(infoTraceFile);
        evidencePayload.trace_attachment_filename = infoTraceFile.name;
        evidencePayload.trace_attachment_content_type = evidenceContentType(infoTraceFile) || 'text/plain';
      }
      const text = (value) => {
        const cleaned = String(value ?? '').trim();
        return cleaned || undefined;
      };
      const payload = (isRoleClarification || isCatalog)
        ? {
            target_role: text(infoForm.target_role),
            catalog: text(infoForm.catalog),
            application: text(infoForm.application),
            source_configuration: text(infoForm.source_configuration),
            access_strategy: text(infoForm.access_strategy),
            copy_source: text(infoForm.copy_source),
            operation: text(infoForm.operation),
            note: text(infoForm.note),
            ...evidencePayload,
            updated_by: user?.email || ticket.requester_name || 'Current User',
          }
        : isTer
        ? {
            username: infoForm.username.trim(),
            termination_date: infoForm.termination_date.trim(),
            updated_by: ticket.requester_name || 'Current User',
          }
        : isPamter
          ? {
              username: infoForm.username.trim(),
              forwarding_recipient: (infoForm.forwarding_recipient || '').trim() || undefined,
              updated_by: ticket.requester_name || 'Current User',
            }
          : {
              new_user: infoForm.new_user.trim(),
              last_name: infoForm.last_name.trim(),
              email: infoForm.email.trim(),
              reference_user: infoForm.reference_user.trim(),
              updated_by: ticket.requester_name || 'Current User',
            };
      await axios.post(`${actionBase}/${id}/info`, payload);
      if (approveOnSelect) {
        setSelectionApproval((current) => ({
          catalog: options.catalog || current?.catalog || '',
          question: stateNow.clarification?.question || '',
          priorPayloadHash: stateNow.menu_change?.proposal?.payload_hash || '',
        }));
        toast.success('Selection saved. HALO is rechecking SAP before applying the catalog change.');
      } else {
        setSelectionApproval(null);
        toast.success('Information saved — the workflow is resuming.');
      }
      fetchTicket();
    } catch (error) {
      console.error('Error submitting workflow info:', error);
      toast.error(workflowInfoError(error));
    } finally {
      setInfoSubmitting(false);
    }
  };

  const handleDecision = async (approved) => {
    if (!ticket || submitting || !canMutate) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${actionBase}/${id}/action`, {
        action: approved ? 'approve' : 'reject',
        approver,
      });
      const nextStage = res.data?.workflow_stage;
      const nextState = res.data?.workflow_state;
      if (nextStage || nextState) {
        setTicket((prev) => ({
          ...prev,
          ...(nextStage ? { workflow_stage: nextStage } : {}),
          ...(nextState ? { workflow_state: nextState } : {}),
        }));
      }
      if (res.data?.status === 'error' || nextStage === WORKFLOW_STAGE.FAILED) {
        const wfFail = resolveWorkflowType(ticket);
        if (nextStage === WORKFLOW_STAGE.ESCALATED || res.data?.workflow_stage === WORKFLOW_STAGE.ESCALATED) {
          toast.warning(
            res.data?.error || getWorkflowEscalationMessage({ ...ticket, workflow_stage: nextStage, workflow_state: nextState || ticket.workflow_state }),
          );
        } else {
          toast.error(
            res.data?.error ||
              (wfFail.id === WORKFLOW_TYPE_TER
                ? 'Termination failed after approval. Check the workflow summary.'
                : wfFail.id === WORKFLOW_TYPE_FFID
                  ? 'Firefighter ID assignment failed after approval. Check the workflow summary.'
                  : 'Provisioning failed after approval. The SAP user may already exist — check the workflow summary.'),
          );
        }
      } else if (nextStage === WORKFLOW_STAGE.ESCALATED) {
        toast.warning(
          res.data?.error ||
            getWorkflowEscalationMessage({
              ...ticket,
              workflow_stage: nextStage,
              workflow_state: nextState || ticket.workflow_state,
            }),
        );
      } else if (approved) {
        const wfOk = resolveWorkflowType(ticket);
        if (wfOk.id === WORKFLOW_TYPE_TER) {
          toast.success('Approved — SAP user terminated.');
        } else if (wfOk.id === WORKFLOW_TYPE_FFID) {
          if (
            nextStage === WORKFLOW_STAGE.AWAITING_USER_CONFIRMATION ||
            res.data?.assignment_emailed
          ) {
            toast.success(
              'Approved — Firefighter ID assigned. An informational notice was emailed (no password).',
            );
          } else {
            toast.success('Approved — Firefighter ID assigned.');
          }
        } else if (wfOk.id === WORKFLOW_TYPE_ROLE_MOD) {
          toast.success(
            'Approved — the exact role delta was applied and is awaiting transport-release review.',
          );
        } else {
          const emailed = Boolean(res.data?.password_emailed);
          const captured = Boolean(res.data?.password_captured);
          if (emailed) {
            toast.success('Approved — user created, roles copied, and credentials emailed.');
          } else if (captured) {
            toast.success('Approved — user created and roles copied. Credentials email did not complete.');
          } else {
            toast.success('Approved — user created and roles copied.');
          }
        }
      } else {
        toast.success('Request rejected.');
      }
      fetchTicket();
    } catch (error) {
      console.error('Error submitting workflow decision:', error);
      toast.error(
        error?.response?.data?.detail ||
          error?.response?.data?.error ||
          'Could not submit decision. Please try again.',
      );
      fetchTicket();
    } finally {
      setSubmitting(false);
    }
  };

  const handleRoleSelection = async (payload) => {
    if (submitting || !canMutate) return;
    const role = payload?.role || selectedRole;
    if (!role) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/workflow/${id}/role-selection`, {
        role,
        selected_by: approver,
        ...(payload?.authChoices ? { auth_choices: payload.authChoices } : {}),
      });
      const nextStage = String(res?.data?.workflow_stage || '');
      if (isRoleModV4(ticket)) {
        // V4: Confirm role is the approval point. The backend re-read the
        // role, rebuilt the exact change, and applied it if nothing changed.
        if (nextStage === 'AWAITING_QA_FUT') {
          toast.success(`Role ${role} confirmed — the exact authorization change was applied in SAP.`);
        } else if (nextStage === 'ROLE_SELECTION_REQUIRED') {
          toast.error('The role changed since the comparison was reviewed. Review the rebuilt comparison and confirm a role again.');
        } else if (nextStage === 'FAILED') {
          toast.error('Role confirmed, but the SAP apply did not complete. Nothing was partially saved.');
        } else {
          toast.success(`Role ${role} confirmed.`);
        }
      } else {
        toast.success('Target role selected — the authorization proposal is ready for approval.');
      }
      setSelectedRole('');
      fetchTicket();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not save the role selection.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMenuChangeApproval = async ({ approved = true, payloadHash, menuStateHash, reviewHash }) => {
    if (submitting || !canApproveMenu) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/workflow/${id}/menu-change-approval`, {
        approved,
        payload_hash: payloadHash,
        menu_state_hash: menuStateHash,
        review_hash: reviewHash || undefined,
      });
      if (approved === false) {
        toast.success('Menu change rejected. No SAP write was performed.');
        fetchTicket();
        return;
      }
      if (res?.data?.revalidation === 'already_assigned') {
        toast.success(res.data.message || 'Already assigned. No change is needed. SAP was not changed.');
        fetchTicket();
        return;
      }
      if (res?.data?.revalidation === 'regenerated' || res?.data?.revalidation === 'changed') {
        toast.success(res.data.message || 'The SAP baseline changed. Review the updated proposal before approving.');
        fetchTicket();
        return;
      }
      const executionPath = res?.data?.execution_path
        || res?.data?.workflow_state?.menu_change?.execution?.execution_path;
      const followUp = res?.data?.workflow_state?.catalog_role_follow_up;
      if (res?.data?.workflow_stage === 'APPLIED' && followUp?.current_stage === 'authorization_review') {
        toast.success('Catalog change verified. Authorization impact is ready for review.');
      } else if (res?.data?.workflow_stage === 'APPLIED' && followUp) {
        toast.error(readableApiDetail(followUp.last_error?.detail) || readableApiDetail(followUp.refresh_execution?.detail) || 'Catalog change verified. Role refresh needs recovery before authorization review.');
      } else if (res?.data?.error === 'EXECUTION_DISABLED') {
        toast.error('Menu change approved, but SAP execution is disabled. No role was changed.');
      } else if (executionPath === 'SIMULATED' || res?.data?.outcome === 'SIMULATED') {
        toast.success('SIMULATED catalog assignment recorded. SAP was not changed. Continue with task validation.');
      } else if (res?.data?.workflow_stage === 'APPLIED' && res?.data?.sap_changed) {
        toast.success('Menu change applied in SAP.');
      } else if (res?.data?.workflow_stage === 'FAILED') {
        toast.error(res?.data?.error || 'SAP rejected the menu change. The role was not changed.');
      } else if (res?.data?.workflow_stage === 'PARTIAL_SUCCESS') {
        toast.error('SAP saved the menu change, but transport recording needs reconciliation.');
      } else if (res?.data?.workflow_stage === 'RECONCILIATION_REQUIRED') {
        toast.error('The SAP result is unknown. Do not retry the menu change until it is reconciled.');
      } else {
        toast.success('Menu change decision recorded.');
      }
      fetchTicket();
    } catch (error) {
      toast.error(readableApiDetail(error?.response?.data?.detail) || 'The menu change could not be approved.');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!selectionApproval || !ticket || submitting) return;
    const verdict = selectionApprovalVerdict(ticket, selectionApproval);
    if (verdict.action === 'wait') return;
    setSelectionApproval(null);
    if (verdict.action === 'approve') {
      handleMenuChangeApproval(verdict.hashes);
    } else if (verdict.reason) {
      toast.error(verdict.reason);
    }
  }, [ticket, selectionApproval, submitting]);

  const handleFioriCatalogExecution = async ({ payloadHash, transportId = '' }) => {
    if (submitting || !canApproveMenu) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/workflow/${id}/fiori-catalog-execution`, {
        confirmation: 'EXECUTE',
        payload_hash: payloadHash,
        transport_id: transportId,
      });
      if (res?.data?.workflow_stage === 'APPLIED') {
        toast.success('Catalog content was verified. The single-role PFCG refresh is the next step.');
      } else if (res?.data?.workflow_stage === 'PARTIAL_SUCCESS') {
        toast.error('The catalog changed, but role or transport verification needs controlled recovery.');
      } else {
        toast.error(res?.data?.error || 'The approved catalog change was not executed.');
      }
      fetchTicket();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'The approved catalog change could not be executed.');
      fetchTicket();
    } finally {
      setSubmitting(false);
    }
  };

  const handleMenuFollowUp = async (payload) => {
    if (submitting || !canMutate) return;
    setSubmitting(true);
    try {
      const evidenceBase64 = payload.evidenceFile ? await fileToBase64(payload.evidenceFile) : undefined;
      const res = await axios.post(
        `${API}/workflow/${id}/menu-change-follow-up`,
        buildMenuFollowUpRequest(payload, { actor: approver, evidenceBase64 }),
      );
      setTicket((prev) => ({
        ...prev,
        workflow_stage: res.data?.workflow_stage || prev.workflow_stage,
        workflow_state: res.data?.workflow_state || prev.workflow_state,
      }));
      const successMessages = {
        approve_authorization_delta: 'Exact authorization delta approved separately from the menu change.',
        reject_authorization_delta: 'Authorization delta rejected. No authorization write was performed.',
        execute_simulated_authorization_delta: 'SIMULATED authorization delta executed. SAP was not changed.',
        approve_role_readiness: 'Role-readiness values approved. SAP is unchanged until they are applied.',
        execute_role_readiness: 'Approved role-readiness maintenance was submitted and read back from SAP.',
        approve_and_apply_role_readiness: 'Role Readiness was applied and verified in SAP. Next: initial profile generation.',
        approve_and_generate_profile: 'The profile was generated and verified in AGR_1016.',
        approve_profile_generation: 'Profile generation approved separately.',
        approve_and_assign_test_role: 'The test role was assigned and read back from SAP.',
        approve_and_apply_cleanup: 'The approved cleanup was applied and read back from SAP.',
        identify_catalog_test_user: 'The SAP test user and current role assignment were verified.',
        approve_catalog_test_user_assignment: 'The test-role assignment was approved separately.',
        execute_catalog_test_user_assignment: 'The test-role assignment was applied and verified.',
        run_catalog_user_comparison: 'User comparison completed and effective access was verified.',
        approve_catalog_corrections: 'The selected cleanup was approved separately.',
        execute_catalog_corrections: 'Approved authorization cleanup was applied and read back.',
      };
      const traceStatus = res.data?.workflow_state?.catalog_role_follow_up?.trace_upload?.status;
      const catalogFollowUp = res.data?.workflow_state?.catalog_role_follow_up || {};
      const comparisonResult = catalogFollowUp.current_stage === 'final_user_comparison'
        ? catalogFollowUp.final_user_comparison
        : (catalogFollowUp.final_user_comparison || catalogFollowUp.initial_user_comparison);
      if (payload.action === 'record_catalog_trace' && traceStatus === 'failed') {
        toast.error('The trace file could not be analyzed. The authorization review is unchanged.');
      } else if (payload.action === 'record_catalog_trace' && traceStatus === 'no_checks') {
        toast.error('No authorization checks were found in that file.');
      } else if (payload.action === 'run_catalog_user_comparison' && comparisonResult?.effective_access_confirmed !== true) {
        toast.error('SAP did not confirm the generated profile as effective for the test user.');
      } else {
        toast.success(payload.outcome === 'NO_AUTH_CHANGE_REQUIRED'
          ? 'Recorded no authorization change. The authorization editor stays closed.'
          : successMessages[payload.action] || 'Follow-up recorded on this ticket.');
      }
      fetchTicket();
    } catch (error) {
      toast.error(readableApiDetail(error?.response?.data?.detail) || 'Could not record the menu follow-up.');
      if ([
        'approve_and_apply_role_readiness',
        'approve_and_generate_profile',
        'approve_and_assign_test_role',
        'approve_and_apply_cleanup',
      ].includes(payload.action)) fetchTicket();
    } finally {
      setSubmitting(false);
    }
  };

  const handleEvidenceDecline = async (payload) => {
    if (submitting || !canMutate) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/workflow/${id}/evidence-confirmation`, {
        checks: payload?.checks || [],
        selected_by: approver,
        decline: true,
      });
      toast.success('Authorization checks declined — escalated for human review. No SAP change was made.');
      fetchTicket();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not escalate the evidence review.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRoleDecline = async (payload) => {
    if (submitting || !canMutate) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/workflow/${id}/role-selection`, {
        role: payload?.role || '',
        selected_by: approver,
        decline: true,
      });
      toast.success('Target role declined — escalated for human review. No SAP change was made.');
      setSelectedRole('');
      fetchTicket();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not escalate the role review.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEvidenceConfirmation = async (payload) => {
    if (submitting || !canMutate) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/workflow/${id}/evidence-confirmation`, {
        checks: payload?.checks || [],
        selected_by: approver,
      });
      toast.success('Authorization checks confirmed. Role analysis is ready.');
      fetchTicket();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not save the evidence choice.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRoleModManualAction = async (action) => {
    if (submitting || !canMutate) return;
    const gateSpec = ROLE_MOD_STAGE_ACTIONS[ticket?.workflow_stage] || null;
    const gateSide = gateSpec?.secondary?.[0] === action ? 'secondary' : 'primary';
    const commentRequired = gateSide === 'secondary'
      ? (gateSpec?.secondaryDescriptionRequired ?? gateSpec?.descriptionRequired)
      : gateSpec?.descriptionRequired;
    const screenshotRequired = gateSide === 'secondary'
      ? (gateSpec?.secondaryEvidenceRequired ?? gateSpec?.evidenceRequired)
      : gateSpec?.evidenceRequired;
    if (screenshotRequired && !manualEvidence) {
      toast.error('This step needs a screenshot attached. The stage did not change.');
      return;
    }
    if (commentRequired && !manualDescription.trim()) {
      toast.error('This step needs a comment. The stage did not change.');
      return;
    }
    setSubmitting(true);
    try {
      const evidenceBase64 = manualEvidence ? await fileToBase64(manualEvidence) : undefined;
      const res = await axios.post(`${API}/workflow/${id}/role-modification-action`, {
        action,
        actor: approver,
        description: manualDescription.trim(),
        evidence_base64: evidenceBase64,
        evidence_filename: manualEvidence?.name,
        evidence_content_type: manualEvidence?.type,
      });
      setTicket((prev) => ({
        ...prev,
        workflow_stage: res.data?.workflow_stage || prev.workflow_stage,
        workflow_state: res.data?.workflow_state || prev.workflow_state,
      }));
      // The response is now persisted on the ticket and rendered under Human decisions.
      setManualDescription('');
      setManualEvidence(null);
      toast.success('Decision recorded — your response is saved on the ticket.');
      fetchTicket();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(
        detail === 'fut_evidence_required' || detail === 'qa_fut_evidence_required'
          ? 'This step needs a screenshot attached. The stage did not change.'
          : detail === 'qa_evidence_required'
            ? 'QA confirmation needs a screenshot. The stage did not change.'
            : detail === 'qa_comment_required' || detail === 'transport_release_comment_required'
              ? 'This step needs a comment. The stage did not change.'
              : detail === 'cab_description_required'
                ? 'CAB needs a description. The stage did not change.'
                : detail === 'prod_evidence_required'
                  ? 'Production confirmation needs a screenshot. The stage did not change.'
                  : detail || 'Could not record this workflow action.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleResolveAccessIssue = async () => {
    if (!ticket || submitting || !canMutate) return;
    const comment = resolveComment.trim();
    if (!comment) {
      toast.error(
        isRoleMod
          ? 'Add review notes, then mark the escalation complete.'
          : ffidNotifyHold === 'future_window'
          ? 'Add a comment describing what you found on the access date, then mark complete.'
          : 'Add a short comment describing how the SAP login issue was resolved.',
      );
      return;
    }
    setSubmitting(true);
    try {
      const resolveUrl = useFfidApi
        ? `${API}/ffid/${id}/resolve-access-issue`
        : `${API}/tickets/${id}/resolve-access-issue`;
      const res = await axios.post(resolveUrl, {
        resolved_by: approver,
        comment,
      });
      const next = res.data?.ticket;
      if (next) {
        setTicket(next);
      } else {
        setTicket((prev) => ({
          ...prev,
          workflow_stage: WORKFLOW_STAGE.DONE,
          status: 'completed',
        }));
      }
      setResolveComment('');
      toast.success(
        res.data?.servicenow_resolved
          ? 'Marked resolved — Halo completed and ServiceNow resolved.'
          : 'Marked resolved — Halo ticket completed.',
      );
      fetchTicket();
    } catch (error) {
      console.error('Error resolving access issue:', error);
      toast.error(
        error?.response?.data?.detail ||
          error?.response?.data?.error ||
          'Could not mark access issue resolved.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!ticket) {
    return <div className="flex items-center justify-center h-full">Ticket not found</div>;
  }

  const stage = ticket.workflow_stage || WORKFLOW_STAGE.EXTRACTING;
  const state = getWorkflowState(ticket);
  const steps = getWorkflowDisplaySteps(ticket);
  const extracted = state.extracted || {};
  const roles = getWorkflowRoles(ticket);
  const sod = getWorkflowSod(ticket);
  const approval = getApproval(ticket);
  const summary = state.summary || ticket.chain_of_thought || '';
  const pending = isPendingApproval(ticket);
  const infoNeeded = needsInfo(ticket);
  const missingFields = getMissingFields(ticket);
  const ffidAlreadyAssigned = isFfidAlreadyAssigned(ticket);
  const heldFfids = getFfidHeldIds(ticket);
  const notificationEmail = getNotificationEmail(ticket);
  const notificationEmailStatus = getNotificationEmailStatus(ticket);
  const wfType = resolveWorkflowType(ticket);
  const isTer = wfType.id === WORKFLOW_TYPE_TER;
  const isPwdReset = wfType.id === WORKFLOW_TYPE_PWD_RESET;
  const isFfid = wfType.id === WORKFLOW_TYPE_FFID;
  const isAccessMod = wfType.id === WORKFLOW_TYPE_ACCESS_MOD;
  const isRoleMod = wfType.id === WORKFLOW_TYPE_ROLE_MOD;
  const isPamter = wfType.id === WORKFLOW_TYPE_PAMTER;
  const isCatalog = state.workflow_agent === WORKFLOW_TYPE_FIORI_CATALOG;
  const routineCatalogChoice = isCatalog && Boolean(catalogStepCopy(state.clarification));
  const roleModDecision = isRoleMod ? getRoleModDecisionSupport(ticket) : null;
  const roleModAnalysisRunning =
    isRoleMod && [WORKFLOW_STAGE.EXTRACTING, WORKFLOW_STAGE.CHECKING].includes(stage);
  // Once any post-approval gate has been recorded, the initial action request is history.
  const roleModHasGateHistory =
    isRoleMod &&
    ['transport_release_approval', 'qa_confirmation', 'fut', 'cab', 'prod_confirmation'].some(
      (key) => state[key] && String(state[key].status || '').toLowerCase() !== 'pending',
    );
  const roleSelectionRequired =
    isRoleMod && stage === WORKFLOW_STAGE.ROLE_SELECTION_REQUIRED;
  const catalogDownstreamHistory =
    Boolean(state.catalog_role_follow_up) &&
    (Array.isArray(state.catalog_lifecycle) ||
      ['transport_release_approval', 'qa_confirmation', 'fut', 'cab', 'prod_confirmation'].some(
        (key) => state[key] && typeof state[key] === 'object',
      ));
  const catalogDownstream = catalogDownstreamHistory && Boolean(ROLE_MOD_STAGE_ACTIONS[stage]);
  const roleModGateSpec = (isRoleMod || catalogDownstream) ? ROLE_MOD_STAGE_ACTIONS[stage] || null : null;
  // Role Modification renders its own human-first decision panels below.
  const showsApproval =
    wfType.showsApproval !== false &&
    !roleSelectionRequired &&
    !isRoleMod &&
    humanApprovalRequired(ticket);
  const ffidNotifyHold = isFfid ? getFfidNotifyHoldReason(ticket) : '';
  const ffidSoftHold = isFfid && isFfidSoftHumanHold(ticket);
  const ffidUserAccessIssue = isFfid && isFfidUserReportedAccessIssue(ticket);
  const ffidAccessIssue = isFfid ? getFfidAccessIssueBanner(ticket) : null;
  const ffidNotificationDisplay = isFfid ? getFfidNotificationEmailDisplay(ticket) : null;
  const ffidNeedsInfo = isFfid ? getFfidNeedsInfoBanner(ticket) : null;
  const sodConflicts = Array.isArray(sod.conflicts) ? sod.conflicts : [];
  const hasConflicts = sod.status === 'conflicts' && sodConflicts.length > 0;
  const sodSkipped = sod.status === 'skipped' || sod.status === 'disabled';
  const sodPending =
    !hasConflicts &&
    !sodSkipped &&
    (['pending', 'unknown', ''].includes(String(sod.status || '').toLowerCase()) ||
      [WORKFLOW_STAGE.EXTRACTING, WORKFLOW_STAGE.CHECKING].includes(stage));
  const approvalStatus = String(approval.status || 'pending').toLowerCase();
  const approvalDecided =
    approvalStatus === 'approved' || approvalStatus === 'rejected';
  const workflowFailed = stage === WORKFLOW_STAGE.FAILED;
  const workflowEscalated = stage === WORKFLOW_STAGE.ESCALATED;
  const roleModHumanClose = isRoleModHumanCloseHold(ticket);
  const roleModEscalationBanner = roleModHumanClose ? getRoleModEscalationBanner(ticket) : null;
  const accessIssueReported = isFfid
    ? isFfidHumanCloseHold(ticket)
    : roleModHumanClose || stage === WORKFLOW_STAGE.ACCESS_ISSUE_REPORTED;
  const userConfirmation = getUserConfirmation(ticket);
  const adminResolutionComment = String(userConfirmation?.admin_comment || '').trim();
  const adminResolvedBy = String(userConfirmation?.admin_resolved_by || '').trim();
  const adminResolvedAt = userConfirmation?.admin_resolved_at;
  const showAdminResolution =
    stage === WORKFLOW_STAGE.DONE && Boolean(adminResolutionComment);
  const approvalBlocked =
    approvalStatus === 'blocked' || workflowFailed || workflowEscalated;
  const approvalInProgress =
    !infoNeeded &&
    !pending &&
    !approvalDecided &&
    !approvalBlocked &&
    [
      WORKFLOW_STAGE.EXTRACTING,
      WORKFLOW_STAGE.CHECKING,
      WORKFLOW_STAGE.PROVISIONING,
      WORKFLOW_STAGE.TERMINATING,
      WORKFLOW_STAGE.AWAITING_USER_CONFIRMATION,
    ].includes(stage);

  return (
    <div className="space-y-6" data-testid="workflow-ticket-detail">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(onFfidRoute ? '/ffid' : '/tickets')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="font-mono text-xs">
                {ticket.ticket_number}
              </Badge>
              <Badge className={`text-xs ${stageBadgeClass(stage, ticket)}`}>
                {getWorkflowStageLabel(ticket)}
              </Badge>
            </div>
            <h1 className="text-2xl font-bold tracking-tight mt-1">{ticket.title}</h1>
          </div>
        </div>
      </div>

      {ffidAlreadyAssigned && (
        <Card className="border-cp-mod bg-cp-inset" data-testid="workflow-already-assigned-banner">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-cp-mod">
              <AlertTriangle className="h-5 w-5" />
              Firefighter already has an assigned ID
            </CardTitle>
            <CardDescription className="text-cp-mod/80">
              {(extracted.firefighter_sap_user || 'This Firefighter')} already has Firefighter ID{' '}
              {heldFfids.join(', ') || '(see analysis)'}. This request was stopped on purpose.
              No information is missing — HALO will not assign a second Firefighter ID for an
              overlapping access window.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-cp-mod/90">
              Free or expire the existing assignment, then submit a new request if another
              Firefighter ID is still needed.
            </p>
          </CardContent>
        </Card>
      )}

      {infoNeeded && !routineCatalogChoice && (
        <Card
          className={isFfid ? 'border-cp-mod bg-cp-inset' : 'border-cp-imp bg-cp-inset'}
          data-testid="workflow-needs-info-banner"
        >
          <CardHeader>
            <CardTitle
              className={`text-lg flex items-center gap-2 ${isFfid ? 'text-cp-mod' : 'text-cp-imp'}`}
            >
              <AlertTriangle className="h-5 w-5" />
              {isFfid ? ffidNeedsInfo.title : 'Missing required information'}
            </CardTitle>
            <CardDescription className={isFfid ? 'text-cp-mod/80' : 'text-cp-imp/80'}>
              {isFfid ? (
                ffidNeedsInfo.description
              ) : isRoleMod ? (
                (missingFields.length > 0
                  ? missingFields
                  : ['Attach SU53 evidence or an authorization trace to continue.']
                ).join(' ')
              ) : isCatalog ? (
                state.clarification?.detail
                || 'Select the exact role, application, catalog, or operation.'
              ) : (
                <>
                  Provide the missing fields below and the workflow will resume automatically.
                  Missing: {(missingFields.length > 0
                    ? missingFields
                    : isTer
                      ? ['Username', 'Termination date']
                      : isPamter
                        ? ['Offboarded user (UPN / email)']
                        : ['User (new user ID)', 'Last name', 'Email', 'Reference user']
                  ).join(', ')}.
                </>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {isFfid ? (
              <p className="text-sm text-cp-imp/90">{ffidNeedsInfo.action}</p>
            ) : isCatalog ? (
              <CatalogClarificationFields
                clarification={state.clarification}
                infoForm={infoForm}
                setInfoForm={setInfoForm}
                canMutate={canMutate}
              />
            ) : isRoleMod ? (
              <div className="grid gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="wf-info-note">Additional information</Label>
                  <Textarea
                    id="wf-info-note"
                    value={infoForm.note}
                    onChange={(e) => setInfoForm((f) => ({ ...f, note: e.target.value }))}
                    placeholder="Describe the missing role, authorization, or correction"
                    disabled={!canMutate}
                    data-testid="workflow-info-note"
                  />
                </div>
                <RoleModEvidenceFields
                  su53File={infoSu53File}
                  traceFile={infoTraceFile}
                  onSu53File={setInfoSu53File}
                  onTraceFile={setInfoTraceFile}
                />
              </div>
            ) : isTer ? (
              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="wf-info-username" className="text-cp-imp">
                    Username
                  </Label>
                  <Input
                    id="wf-info-username"
                    value={infoForm.username}
                    onChange={(e) => setInfoForm((f) => ({ ...f, username: e.target.value }))}
                    placeholder="e.g. JDOE"
                    className="bg-cp-surface"
                    disabled={!canMutate}
                    data-testid="workflow-info-username"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="wf-info-term-date" className="text-cp-imp">
                    Termination date
                  </Label>
                  <Input
                    id="wf-info-term-date"
                    value={infoForm.termination_date}
                    onChange={(e) =>
                      setInfoForm((f) => ({ ...f, termination_date: e.target.value }))
                    }
                    placeholder="e.g. 2026-08-01 or immediately"
                    className="bg-cp-surface"
                    disabled={!canMutate}
                    data-testid="workflow-info-term-date"
                  />
                </div>
              </div>
            ) : wfType.id === WORKFLOW_TYPE_PAMTER ? (
              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="wf-info-pamter-user" className="text-cp-imp">
                    User (UPN / email)
                  </Label>
                  <Input
                    id="wf-info-pamter-user"
                    value={infoForm.username}
                    onChange={(e) => setInfoForm((f) => ({ ...f, username: e.target.value }))}
                    placeholder="e.g. jane.doe@contoso.com"
                    className="bg-cp-surface"
                    disabled={!canUpdateTicket}
                    data-testid="workflow-info-pamter-user"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="wf-info-pamter-forward">
                    Forwarding recipient (optional — Flow B only)
                  </Label>
                  <Input
                    id="wf-info-pamter-forward"
                    value={infoForm.forwarding_recipient || ''}
                    onChange={(e) =>
                      setInfoForm((f) => ({ ...f, forwarding_recipient: e.target.value }))
                    }
                    placeholder="e.g. manager@contoso.com"
                    className="bg-cp-surface"
                    disabled={!canUpdateTicket}
                    data-testid="workflow-info-pamter-forward"
                  />
                </div>
              </div>
            ) : isRoleMod ? null : (
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
              <div className="space-y-1.5">
                <Label htmlFor="wf-info-user" className="text-cp-imp">
                  User (new user ID)
                </Label>
                <Input
                  id="wf-info-user"
                  value={infoForm.new_user}
                  onChange={(e) => setInfoForm((f) => ({ ...f, new_user: e.target.value }))}
                  placeholder="e.g. TESTUSER02"
                  className="bg-cp-surface"
                  disabled={!canMutate}
                  data-testid="workflow-info-user"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="wf-info-lastname" className="text-cp-imp">
                  Last name
                </Label>
                <Input
                  id="wf-info-lastname"
                  value={infoForm.last_name}
                  onChange={(e) => setInfoForm((f) => ({ ...f, last_name: e.target.value }))}
                  placeholder="e.g. Doe"
                  className="bg-cp-surface"
                  disabled={!canMutate}
                  data-testid="workflow-info-lastname"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="wf-info-email" className="text-cp-imp">
                  Email
                </Label>
                <Input
                  id="wf-info-email"
                  type="email"
                  value={infoForm.email}
                  onChange={(e) => setInfoForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="e.g. new.user@company.com"
                  className="bg-cp-surface"
                  disabled={!canMutate}
                  data-testid="workflow-info-email"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="wf-info-ref" className="text-cp-imp">
                  Reference user
                </Label>
                <Input
                  id="wf-info-ref"
                  value={infoForm.reference_user}
                  onChange={(e) =>
                    setInfoForm((f) => ({ ...f, reference_user: e.target.value }))
                  }
                  placeholder="e.g. KDOOLMITTA"
                  className="bg-cp-surface"
                  disabled={!canMutate}
                  data-testid="workflow-info-ref"
                />
              </div>
            </div>
            )}
            {!isFfid && (
            <span title={permissionDeniedTitle(canMutate, 'update ticket workflow details')}>
              <Button
                onClick={handleInfoSubmit}
                disabled={infoSubmitting || !canMutate || (isCatalog && !catalogSelectionReady(state.clarification || {}, infoForm))}
                className="bg-cp-imp hover:bg-cp-imp"
                data-testid="workflow-info-submit"
              >
                {infoSubmitting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                )}
                Save & Resume Workflow
              </Button>
            </span>
            )}
          </CardContent>
        </Card>
      )}

      {accessIssueReported && (
        <HumanEscalationClosePanel
          title={
            roleModEscalationBanner?.title
            || ffidAccessIssue?.title
            || 'Human escalation required'
          }
          description={
            roleModEscalationBanner?.description
            || (isFfid
              ? ffidAccessIssue?.description
              : 'The end user reported they cannot log in to SAP after provisioning. A human must investigate access, then mark resolved to complete the Halo ticket (and resolve ServiceNow when applicable).')
          }
          comment={resolveComment}
          onComment={setResolveComment}
          onSubmit={handleResolveAccessIssue}
          submitting={submitting}
          canMutate={canMutate}
          soft={Boolean(roleModEscalationBanner) || (isFfid && ffidSoftHold)}
          commentLabel={roleModEscalationBanner?.commentLabel || 'Resolution comment'}
          placeholder={
            roleModEscalationBanner?.placeholder
            || (ffidNotifyHold === 'future_window'
              ? 'What did you confirm on the access date? (required)'
              : ffidNotifyHold
              ? 'How did you confirm the Firefighter was notified? (required)'
              : ffidUserAccessIssue
              ? 'How was the Firefighter ID issue fixed? (required)'
              : 'How was the SAP login issue fixed? (required)')
          }
          buttonLabel={
            roleModEscalationBanner?.buttonLabel
            || (isFfid
              ? ffidNotifyHold === 'future_window'
                ? 'Mark complete'
                : 'Mark Firefighter ID issue resolved'
              : 'Mark SAP login resolved')
          }
        />
      )}

      {showAdminResolution && (
        <Card
          className="border-cp-ok bg-[color-mix(in_srgb,var(--ok)_10%,var(--surface))]"
          data-testid="workflow-admin-resolution-note"
        >
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-cp-ok">
              <CheckCircle2 className="h-5 w-5" />
              Human resolution recorded
            </CardTitle>
            <CardDescription>
              {[adminResolvedBy, adminResolvedAt ? new Date(adminResolvedAt).toLocaleString() : '']
                .filter(Boolean)
                .join(' · ') || 'Admin marked SAP login resolved'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap">{adminResolutionComment}</p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Workflow Progress</CardTitle>
          <CardDescription>
            {wfType.label}
            {workflowProgressDetail(ticket)
              ? ` — ${workflowProgressDetail(ticket)}`
              : menuProgressNote(ticket) === 'rejected'
              ? ' — rejected'
              : menuProgressNote(ticket) === 'technical failure'
                ? ' — technical failure'
                : stage === WORKFLOW_STAGE.DONE
                  ? ' — complete'
                  : menuProgressNote(ticket) === 'pending-gates'
                  ? ' — development change recorded; QA / FUT, CAB, and production confirmation are still pending'
                  : infoNeeded
              ? ' — waiting for required information'
              : pending
                ? ' — pre-approval analysis'
                : stage === WORKFLOW_STAGE.OFFBOARDING
                    ? ' — offboarding in progress'
                    : stage === WORKFLOW_STAGE.SCHEDULED
                      ? isFfid
                        ? ' — awaiting confirmation; human escalation started (future window)'
                        : ' — deactivation scheduled; the cron will complete this on the effective date'
                      : stage === WORKFLOW_STAGE.ACCESS_ISSUE_REPORTED
                        ? isFfid && ffidNotifyHold === 'future_window'
                          ? ' — awaiting confirmation; human escalation started (future window)'
                          : ' — awaiting confirmation; human escalation started'
                        : stage === WORKFLOW_STAGE.REJECTED
                          ? ' — rejected'
                          : stage === WORKFLOW_STAGE.ESCALATED
                            ? ' — escalated for human review'
                            : stage === WORKFLOW_STAGE.FAILED
                              ? ' — failed'
                              : ' — in progress'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <WorkflowTaskGraph steps={steps} currentStep={getWorkflowDisplayCurrentStep(ticket)} />
        </CardContent>
      </Card>

      {!isPamter && (
        <NupAuditPackageCard
          ticketNumber={ticket.ticket_number || id}
          refreshKey={`${ticket.workflow_stage || ''}:${approvalStatus}:${ticket.updated_at || ''}`}
          embeddedTrail={Array.isArray(state.audit_trail) ? state.audit_trail : []}
        />
      )}

      {routineCatalogChoice && (
        <CatalogClarificationFields
          clarification={state.clarification}
          infoForm={infoForm}
          setInfoForm={setInfoForm}
          canMutate={canMutate}
          submitting={infoSubmitting}
          onContinue={handleInfoSubmit}
          canApprove={canApproveMenu}
          approvalIntent={Boolean(selectionApproval)}
        />
      )}

      <MenuChangeApproval
        ticket={ticket}
        onApprove={handleMenuChangeApproval}
        onReject={(hashes) => handleMenuChangeApproval({ ...hashes, approved: false })}
        onExecute={handleFioriCatalogExecution}
        submitting={submitting}
        canMutate={canApproveMenu}
        automaticApproval={Boolean(selectionApproval) && selectionApprovalVerdict(ticket, selectionApproval).action === 'approve'}
      />

      <CatalogRoleFollowUp
        ticket={ticket}
        orgSuggestions={orgSuggestions}
        onSubmit={handleMenuFollowUp}
        submitting={submitting}
        canMutate={canMutate}
      />

      {!ticket?.workflow_state?.catalog_role_follow_up && (
      <MenuChangeFollowUp
        ticket={ticket}
        onSubmit={handleMenuFollowUp}
        submitting={submitting}
        canMutate={canMutate}
        canApprove={canApproveMenu}
      />
      )}

      {isRoleMod && (
        <>
          <RoleModTransportCreated ticket={ticket} />

          {workflowFailed && !isRoleModV4(ticket) && <RoleModFailureCard ticket={ticket} />}

          <RoleModV4Analysis
            ticket={ticket}
            selectedRole={selectedRole}
            onSelectRole={setSelectedRole}
            onConfirmSelection={handleRoleSelection}
            onConfirmEvidence={handleEvidenceConfirmation}
            onDeclineEvidence={handleEvidenceDecline}
            onDeclineRole={handleRoleDecline}
            submitting={submitting}
            canMutate={canMutate}
          />

          <RoleModRoleAnalysis
            ticket={ticket}
            selectedRole={selectedRole}
            onSelectRole={setSelectedRole}
            onConfirmSelection={handleRoleSelection}
            submitting={submitting}
            canMutate={canMutate}
          />

          <RoleModActionRequest
            ticket={ticket}
            decision={roleModDecision}
            onApprove={() => handleDecision(true)}
            onReject={() => handleDecision(false)}
            submitting={submitting}
            canMutate={canMutate}
            analysisRunning={roleModAnalysisRunning}
            gateSpec={roleModGateSpec}
            gateDescription={manualDescription}
            onGateDescriptionChange={setManualDescription}
            gateEvidence={manualEvidence}
            onGateEvidenceChange={setManualEvidence}
            onGateAction={handleRoleModManualAction}
          />

          <RoleModDecisionHistory ticket={ticket} />

          <RoleModDocuments ticket={ticket} />
        </>
      )}

      {catalogDownstreamHistory && (
        <>
          {catalogDownstream && (
            <RoleModActionRequest
              ticket={ticket}
              decision={null}
              submitting={submitting}
              canMutate={canMutate}
              gateSpec={roleModGateSpec}
              gateDescription={manualDescription}
              onGateDescriptionChange={setManualDescription}
              gateEvidence={manualEvidence}
              onGateEvidenceChange={setManualEvidence}
              onGateAction={handleRoleModManualAction}
            />
          )}
          <RoleModDecisionHistory ticket={ticket} />
          <RoleModDocuments ticket={ticket} />
        </>
      )}

      {!isRoleMod && !isCatalog && (
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Request Details</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3">
            {isPamter ? (
              <>
                <InfoTile
                  icon={Users}
                  label="User"
                  value={extracted.username}
                  missing={infoNeeded && !extracted.username}
                />
                {extracted.forwarding_recipient ? (
                  <InfoTile
                    icon={Mail}
                    label="Forward to"
                    value={extracted.forwarding_recipient}
                  />
                ) : null}
              </>
            ) : isTer ? (
              <>
                <InfoTile
                  icon={Users}
                  label="Username"
                  value={extracted.username}
                  missing={infoNeeded && !extracted.username}
                />
                <InfoTile
                  icon={ClipboardList}
                  label="Termination date"
                  value={extracted.termination_date}
                  missing={infoNeeded && !extracted.termination_date}
                />
                {extracted.termination_mode && (
                  <InfoTile
                    icon={ClipboardList}
                    label="Mode"
                    value={
                      extracted.termination_mode === 'scheduled'
                        ? 'Scheduled (validity-to only)'
                        : 'Immediate (revoke roles + terminate)'
                    }
                  />
                )}
              </>
            ) : isPwdReset ? (
              <>
                <InfoTile
                  icon={Users}
                  label="Username"
                  value={extracted.username}
                  missing={infoNeeded && !extracted.username}
                />
                <InfoTile
                  icon={Mail}
                  label="Email"
                  value={extracted.email}
                  missing={infoNeeded && !extracted.email}
                />
              </>
            ) : isFfid ? (
              <>
                <InfoTile
                  icon={Users}
                  label="Requester"
                  value={extracted.requester_name}
                />
                <InfoTile
                  icon={Users}
                  label="Firefighter SAP user"
                  value={extracted.firefighter_sap_user}
                  missing={infoNeeded && !extracted.firefighter_sap_user}
                />
                <InfoTile
                  icon={ClipboardList}
                  label="Reason / activity"
                  value={extracted.reason}
                  missing={infoNeeded && !extracted.reason}
                />
                <InfoTile
                  icon={ClipboardList}
                  label="Requested T-codes"
                  value={extracted.tcodes}
                  missing={infoNeeded && !extracted.tcodes}
                />
                <InfoTile
                  icon={ClipboardList}
                  label="Access window"
                  value={
                    state.dates?.valid_from && state.dates?.valid_to
                      ? `${state.dates.valid_from} → ${state.dates.valid_to}`
                      : null
                  }
                />
                <InfoTile
                  icon={Mail}
                  label="Assignment notice"
                  value={
                    state.window_kind === 'future'
                      ? 'Reservation emailed — human escalation started (owner closes on access date)'
                      : state.window_kind === 'same_day'
                        ? 'Proceed emailed — waiting for user confirmation'
                        : null
                  }
                />
                <InfoTile
                  icon={ShieldCheck}
                  label={state.provisioned?.ffid ? 'Assigned FFID' : 'Proposed FFID'}
                  value={state.provisioned?.ffid || state.proposed?.ffid}
                />
                <InfoTile
                  icon={ShieldCheck}
                  label="Available FFIDs (at analysis)"
                  value={
                    (state.pool?.available || []).length
                      ? (state.pool.available || [])
                          .map((row) => row.ffid)
                          .filter(Boolean)
                          .join(', ')
                      : '(none)'
                  }
                />
                <InfoTile
                  icon={AlertTriangle}
                  label="Occupied FFIDs (at analysis)"
                  value={
                    (state.pool?.occupied || []).length
                      ? (state.pool.occupied || [])
                          .map((row) => `${row.ffid} → ${row.firefighter}`)
                          .join(', ')
                      : '(none)'
                  }
                />
                <InfoTile
                  icon={Mail}
                  label="Notification email"
                  value={
                    ffidNotificationDisplay
                      ? ffidNotificationDisplay.value
                      : notificationEmail
                        ? notificationEmailStatus === 'sent'
                          ? `${notificationEmail} (sent)`
                          : notificationEmail
                        : null
                  }
                  missing={Boolean(ffidNotificationDisplay?.missing)}
                />
                {state.already_assigned?.length ? (
                  <InfoTile
                    icon={AlertTriangle}
                    label="Existing assignment"
                    value={`${extracted.firefighter_sap_user || 'This user'} already has ${(
                      state.already_assigned || []
                    )
                      .map((row) => row.ffid)
                      .filter(Boolean)
                      .join(', ')}`}
                  />
                ) : null}
                <InfoTile
                  icon={ShieldCheck}
                  label="FFID availability"
                  value={
                    state.proposed?.availability
                      ? `${state.proposed.availability}${
                          state.proposed.occupancy ? ` (${state.proposed.occupancy})` : ''
                        }`
                      : null
                  }
                />
                <InfoTile
                  icon={AlertTriangle}
                  label="T-code / access validation"
                  value={
                    state.tcode_check?.performed
                      ? state.tcode_check.status === 'ok'
                        ? 'OK — requested T-codes present'
                        : state.tcode_check.status === 'configuration_exception'
                          ? `Exception — missing ${(state.tcode_check.missing || []).join(', ')}`
                          : state.tcode_check.status
                      : state.tcode_check?.status === 'skipped'
                        ? `Skipped (${state.tcode_check.reason || 'n/a'})`
                        : 'Not performed'
                  }
                />
              </>
            ) : isAccessMod ? (
              <>
                <InfoTile
                  icon={Users}
                  label="User"
                  value={extracted.target_user}
                  missing={infoNeeded && !extracted.target_user}
                />
                <InfoTile
                  icon={Mail}
                  label="Email"
                  value={extracted.target_email || extracted.email}
                  missing={infoNeeded && !(extracted.target_email || extracted.email)}
                />
                <InfoTile
                  icon={Users}
                  label="Reference User (optional)"
                  value={extracted.reference_user}
                />
              </>
            ) : (
              <>
                <InfoTile
                  icon={UserPlus}
                  label="New User"
                  value={extracted.new_user}
                  missing={infoNeeded && !extracted.new_user}
                />
                <InfoTile
                  icon={Users}
                  label="Last Name"
                  value={extracted.last_name}
                  missing={infoNeeded && !extracted.last_name}
                />
                <InfoTile
                  icon={Mail}
                  label="Email"
                  value={extracted.email}
                  missing={infoNeeded && !extracted.email}
                />
                <InfoTile
                  icon={Users}
                  label="Reference User"
                  value={extracted.reference_user}
                  missing={infoNeeded && !extracted.reference_user}
                />
              </>
            )}
          </CardContent>
        </Card>

        {wfType.showsRoles && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <ClipboardList className="h-5 w-5" />
              {wfType.id === WORKFLOW_TYPE_ACCESS_MOD ? 'Role Changes' : 'Roles to Copy'}
            </CardTitle>
            {wfType.id !== WORKFLOW_TYPE_ACCESS_MOD && (
              <CardDescription>From reference user {extracted.reference_user || ''}</CardDescription>
            )}
          </CardHeader>
          <CardContent>
            {roles.length > 0 ? (
              <ul className="space-y-1 text-sm">
                {roles.map((role, idx) => {
                  const isAdd = role.startsWith('+ ');
                  const isRemove = role.startsWith('- ');
                  const label = isAdd || isRemove ? role.slice(2) : role;
                  return (
                    <li key={idx} className="flex items-start gap-2">
                      <span
                        className={`mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 ${
                          isAdd ? 'bg-cp-ok' : isRemove ? 'bg-cp-crit' : 'bg-cp-indigo'
                        }`}
                      />
                      <span
                        className={`font-mono ${
                          isAdd ? 'text-cp-ok' : isRemove ? 'text-cp-crit' : ''
                        }`}
                      >
                        {isAdd ? '+ ' : isRemove ? '− ' : ''}
                        {label}
                      </span>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">
                {infoNeeded
                  ? 'Roles were not fetched — required information is missing.'
                  : [WORKFLOW_STAGE.EXTRACTING, WORKFLOW_STAGE.CHECKING].includes(stage)
                    ? 'Fetching reference user roles…'
                    : 'No roles found on the reference user.'}
              </p>
            )}
          </CardContent>
        </Card>
        )}

      </div>
      )}

      {wfType.showsSod && (
      <Card
        className={
          hasConflicts
            ? 'border-cp-crit'
            : sodSkipped || sodPending
              ? 'border-muted'
              : 'border-cp-ok'
        }
      >
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            {hasConflicts ? (
              <ShieldAlert className="h-5 w-5 text-cp-crit" />
            ) : sodSkipped || sodPending ? (
              <ShieldCheck className="h-5 w-5 text-muted-foreground" />
            ) : (
              <ShieldCheck className="h-5 w-5 text-cp-ok" />
            )}
            Segregation of Duties (SoD) Check
          </CardTitle>
        </CardHeader>
        <CardContent>
          {hasConflicts ? (
            <div className="space-y-2">
              <Badge className="bg-cp-crit text-white">
                {sodConflicts.length} conflict{sodConflicts.length === 1 ? '' : 's'} detected
              </Badge>
              <ul className="space-y-1 text-sm">
                {sodConflicts.map((c, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-cp-crit">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : sodSkipped ? (
            <p className="text-sm text-muted-foreground">
              {sod.status === 'disabled'
                ? 'SoD check is disabled for this request.'
                : 'SoD check was not run.'}
            </p>
          ) : sodPending ? (
            <p className="text-sm text-muted-foreground">
              {String(sod.status || '').toLowerCase() === 'unknown'
                ? 'SoD result was not captured in the analysis summary.'
                : 'SoD check pending.'}
            </p>
          ) : (
            <div className="flex items-center gap-2 text-cp-ok text-sm">
              <CheckCircle2 className="h-4 w-4" />
              No SoD conflicts detected.
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {!showsApproval && !isRoleMod && !(isCatalog && state.menu_change?.proposal) && summary && (
        <Card data-testid="workflow-analysis-summary">
          <CardHeader>
            <CardTitle className="text-lg">Analysis</CardTitle>
            <CardDescription>
              {isPamter
                ? 'PAM Health offboarding analysis — no human approval required.'
                : 'Workflow analysis summary.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <pre className="whitespace-pre-wrap text-sm bg-muted rounded-lg p-4 max-h-96 overflow-auto font-sans">
              {summary}
            </pre>
          </CardContent>
        </Card>
      )}

      {showsApproval && (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Approval</CardTitle>
          <CardDescription>
            {ffidAlreadyAssigned
              ? `${extracted.firefighter_sap_user || 'This Firefighter'} already has Firefighter ID ${
                  heldFfids.join(', ') || '(see analysis)'
                }. Approval stays blocked — this is not missing ticket data.`
              : infoNeeded
              ? isFfid
                ? 'This ticket will not resume here. Submit a new Firefighter ID request with a complete form attachment.'
                : 'Approval is blocked until required ticket fields are provided.'
              : pending
                ? isTer
                  ? 'Review the analysis above and approve to terminate the user.'
                  : isFfid
                    ? 'Review the proposed Firefighter ID and validation below, then approve. GRC assignment and the user notice run only after this approval.'
                    : isRoleMod
                      ? 'Review the evidence, target existing role, affected users, and exact authorization addition. Approval changes role content; it does not grant a role.'
                    : 'Review the analysis above and approve to provision the user.'
                : approvalDecided
                  ? workflowEscalated
                    ? 'Approved — escalated for human review'
                    : `Decision: ${approval.status}`
                  : approvalBlocked
                    ? workflowFailed
                      ? isTer
                        ? 'Termination cannot proceed — see the failure details above.'
                        : 'Provisioning cannot proceed — see the failure details above.'
                      : 'Approval is blocked for this request.'
                    : 'Approval will unlock after the analysis finishes.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {summary && (
            <pre className="whitespace-pre-wrap text-sm bg-muted rounded-lg p-4 max-h-72 overflow-auto font-sans">
              {summary}
            </pre>
          )}

          {ffidAlreadyAssigned ? (
            <div className="flex items-center gap-2 rounded-lg border border-cp-imp bg-cp-inset p-3 text-cp-imp">
              <AlertTriangle className="h-5 w-5 shrink-0" />
              <span className="text-sm font-medium">
                {(extracted.firefighter_sap_user || 'This Firefighter')} already has Firefighter ID{' '}
                {heldFfids.join(', ') || '(see analysis)'}. No second ID will be assigned.
              </span>
            </div>
          ) : infoNeeded && !routineCatalogChoice ? (
            <div className="flex items-center gap-2 rounded-lg border border-cp-imp bg-cp-inset p-3 text-cp-imp">
              <AlertTriangle className="h-5 w-5 shrink-0" />
              <span className="text-sm font-medium">
                {isFfid
                  ? 'There are no fields to fill on this page. Attach a complete form to a new Firefighter ID ticket and submit again.'
                  : 'Fill in the missing information above — the analysis will re-run and approval will unlock automatically.'}
              </span>
            </div>
          ) : pending ? (
            <div className="flex items-center gap-3">
              <span title={permissionDeniedTitle(canMutate, 'approve this request')}>
                <Button
                  onClick={() => handleDecision(true)}
                  disabled={submitting || !canMutate}
                  className="bg-cp-ok hover:bg-cp-ok"
                  data-testid="workflow-approve-btn"
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                  )}
                  {isTer
                    ? 'Approve & Terminate'
                    : isFfid
                      ? 'Approve FFID request'
                      : isRoleMod
                        ? 'Approve exact role change'
                      : 'Approve & Provision'}
                </Button>
              </span>
              <span title={permissionDeniedTitle(canMutate, 'reject this request')}>
                <Button
                  variant="outline"
                  onClick={() => handleDecision(false)}
                  disabled={submitting || !canMutate}
                  className="border-cp-crit text-cp-crit hover:bg-cp-inset"
                  data-testid="workflow-reject-btn"
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Reject
                </Button>
              </span>
              {hasConflicts && (
                <span className="text-xs text-cp-mod flex items-center gap-1">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  SoD conflicts present — review before approving.
                </span>
              )}
            </div>
          ) : approvalInProgress ? (
            <div className="flex items-center gap-2 rounded-lg border border-muted bg-muted/40 p-3 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin shrink-0" />
              <span className="text-sm font-medium">
                Waiting for analysis to finish before approval is available.
              </span>
            </div>
          ) : workflowEscalated ? (
            <div className="flex items-start gap-2 rounded-lg border border-cp-imp bg-cp-inset p-3 text-cp-imp">
              <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
              <div className="text-sm space-y-1">
                <p className="font-medium">Escalated for human review</p>
                <p className="text-muted-foreground">
                  {getWorkflowEscalationMessage(ticket)}
                </p>
              </div>
            </div>
          ) : approvalBlocked ? (
            <div className="flex items-center gap-2 rounded-lg border border-cp-crit bg-cp-inset p-3 text-cp-crit">
              <XCircle className="h-5 w-5 shrink-0" />
              <span className="text-sm font-medium">
                {(state.provisioning_error ||
                  state.pool?.error ||
                  state.termination?.error ||
                  state.provisioning?.create_user?.error ||
                  (isRoleMod && getRoleModFailureMessage(ticket)) ||
                  (approvalDecided
                    ? 'Workflow failed after approval.'
                    : 'Workflow failed before approval.'))}
              </span>
            </div>
          ) : approvalStatus === 'approved' ? (
            <div className="flex items-center gap-2 rounded-lg border border-cp-ok bg-cp-inset p-3 text-cp-ok">
              <CheckCircle2 className="h-5 w-5" />
              <span className="text-sm font-medium">
                Approved
                {approval.by ? ` by ${approval.by}` : ''}
                {approval.at ? ` • ${new Date(approval.at).toLocaleString()}` : ''}
              </span>
            </div>
          ) : approvalStatus === 'rejected' || stage === WORKFLOW_STAGE.REJECTED ? (
            <div className="flex items-center gap-2 rounded-lg border border-cp-crit bg-cp-inset p-3 text-cp-crit">
              <XCircle className="h-5 w-5" />
              <span className="text-sm font-medium">
                Rejected
                {approval.by ? ` by ${approval.by}` : ''}
                {approval.at ? ` • ${new Date(approval.at).toLocaleString()}` : ''}
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-2 rounded-lg border border-muted bg-muted/40 p-3 text-muted-foreground">
              <CheckCircle2 className="h-5 w-5 shrink-0" />
              <span className="text-sm font-medium">
                Analysis complete — no human approval step in this workflow template.
              </span>
            </div>
          )}
        </CardContent>
      </Card>
      )}
    </div>
  );
}
